// AttendanceReportsPanel — date/month/year attendance registers for
// students and staff, viewable on screen and downloadable as Excel.
// Reuses StaffAttendanceDashboard.jsx's status palette and table styling.
import { useEffect, useState } from 'react';
import {
  getStudentAttendanceReport, getStaffAttendanceReport,
  exportStudentAttendance, exportStaffAttendance, getClasses,
} from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';

const STATUS_TEXT_CLS = {
  good:     'text-success',
  critical: 'text-critical',
  warn:     'text-caution',
  muted:    'text-ink-soft',
};

const CELL_CLASS = { P: STATUS_TEXT_CLS.good, A: STATUS_TEXT_CLS.critical, L: STATUS_TEXT_CLS.warn, H: STATUS_TEXT_CLS.muted, 'P*': STATUS_TEXT_CLS.warn };

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthNow = () => new Date().toISOString().slice(0, 7);
const yearNow  = () => new Date().getFullYear();

export default function AttendanceReportsPanel() {
  const [type,    setType]    = useState('student'); // 'student' | 'staff'
  const [scope,   setScope]   = useState('month');    // 'date' | 'month' | 'year'
  const [dateVal, setDateVal] = useState(todayISO());
  const [monthVal, setMonthVal] = useState(monthNow());
  const [yearVal, setYearVal] = useState(yearNow());
  const [classId, setClassId] = useState('all');
  const [classes, setClasses] = useState([]);
  const [report,  setReport]  = useState(null);
  const [msg,     setMsg]     = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { getClasses().then(setClasses).catch(() => {}); }, []);
  useEffect(() => { load(); }, [type, scope, dateVal, monthVal, yearVal, classId]);

  function scopeParams() {
    const params = scope === 'date' ? { date: dateVal } : scope === 'month' ? { month: monthVal } : { year: String(yearVal) };
    if (type === 'student') params.class_id = classId;
    return params;
  }

  async function load() {
    setLoading(true); setMsg('');
    try {
      const params = scopeParams();
      const data = type === 'student' ? await getStudentAttendanceReport(params) : await getStaffAttendanceReport(params);
      setReport(data);
    } catch (err) {
      setReport(null);
      setMsg('Failed to load report: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }

  async function handleDownload() {
    setDownloading(true); setMsg('');
    try {
      const params = scopeParams();
      const blob = type === 'student' ? await exportStudentAttendance(params) : await exportStaffAttendance(params);
      const value = scope === 'date' ? dateVal : scope === 'month' ? monthVal : yearVal;
      const filename = `${type}_attendance_${scope}_${value}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMsg('Failed to download: ' + (err.response?.data?.error || err.message));
    } finally { setDownloading(false); }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-transit";

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Attendance Reports</h2>

      <div className="flex gap-2 items-center flex-wrap mb-3">
        {['student', 'staff'].map(t => (
          <Button key={t} variant={type === t ? 'primary' : 'secondary'} onClick={() => setType(t)}>
            {t === 'student' ? 'Student Attendance' : 'Staff Attendance'}
          </Button>
        ))}
      </div>

      <div className="flex gap-2 items-center flex-wrap mb-4">
        {['date', 'month', 'year'].map(s => (
          <Button key={s} variant={scope === s ? 'primary' : 'secondary'} onClick={() => setScope(s)}>
            {s[0].toUpperCase() + s.slice(1)}
          </Button>
        ))}

        {scope === 'date'  && <input type="date"  value={dateVal}  onChange={e => setDateVal(e.target.value)} className={inputCls} />}
        {scope === 'month' && <input type="month" value={monthVal} onChange={e => setMonthVal(e.target.value)} className={inputCls} />}
        {scope === 'year'  && <input type="number" value={yearVal} onChange={e => setYearVal(e.target.value)} className={`${inputCls} w-[90px]`} min="2000" max="2100" />}

        {type === 'student' && (
          <select value={classId} onChange={e => setClassId(e.target.value)} className={inputCls}>
            <option value="all">All Classes</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        <Button className="ml-auto" onClick={handleDownload} disabled={downloading || !report}>
          {downloading ? 'Preparing…' : 'Download Excel'}
        </Button>
      </div>

      {report && report.working_days != null && (
        <p className="text-sm text-ink-soft my-1">School working days counted this year (Mon–Sat, minus holidays): {report.working_days}</p>
      )}
      <p className="text-sm text-ink-soft my-1">
        Legend: <span className={STATUS_TEXT_CLS.good}>P</span> Present · <span className={STATUS_TEXT_CLS.critical}>A</span> Absent ·{' '}
        <span className={STATUS_TEXT_CLS.warn}>L</span> Late/Flagged · <span className={STATUS_TEXT_CLS.muted}>H</span> Holiday · blank = Not Marked
      </p>

      {msg && <p className="text-critical text-sm font-bold">{msg}</p>}
      {loading && <p className="text-ink-soft text-sm">Loading…</p>}

      {!loading && report && (
        <Card className="mt-4">
          <CardHeader title="Report" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                  {report.columns.map(c => <th key={c.key} className="px-4 py-2.5 whitespace-nowrap sticky top-0 bg-paper">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, i) => (
                  <tr key={i} className="border-t border-line-soft hover:bg-transit-tint/40">
                    {report.columns.map(c => (
                      <td key={c.key} className={`px-4 py-3 whitespace-nowrap ${CELL_CLASS[row[c.key]] || ''} ${CELL_CLASS[row[c.key]] ? 'font-bold' : ''}`}>
                        {row[c.key] === '' || row[c.key] == null ? '—' : row[c.key]}
                        {c.key === 'attendance_pct' && row[c.key] != null ? '%' : ''}
                      </td>
                    ))}
                  </tr>
                ))}
                {!report.rows.length && (
                  <tr><td colSpan={report.columns.length} className="px-4 py-8 text-center text-ink-soft">No data for this selection</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
